import React, { useState, useMemo } from "react";
import { ButtonComp, CardComp } from "../../../../components";
import {
  useGetCompanyContributionCategoriesQuery,
  useImportContributionCategoriesMutation,
} from "../../../../store/admin/contribution";
import toast from "react-hot-toast";
import { formatMoney } from "../../../../utils";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import moment from "moment";

const ImportContributionCategoriesModal = ({ handleClose }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error: loadError,
    refetch,
  } = useGetCompanyContributionCategoriesQuery();
  const [importCategories, { isLoading: isImporting }] =
    useImportContributionCategoriesMutation();

  const loadErrorMessage =
    loadError?.data?.message ||
    loadError?.data?.error ||
    loadError?.message ||
    loadError?.error ||
    (typeof loadError === "string" ? loadError : null) ||
    "Could not load categories from other branches. Please try again.";

  const companyCategories = Array.isArray(data) ? data : [];

  // Filter categories by search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return companyCategories;
    return companyCategories.filter((category) =>
      category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companyCategories, searchTerm]);

  const handleToggleSelection = (categoryId) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      }
      return [...prev, categoryId];
    });
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === filteredCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(filteredCategories.map((cat) => cat.id));
    }
  };

  const handleImport = async () => {
    if (selectedCategories.length === 0) {
      return toast.error("Please select at least one contribution category to import");
    }

    const response = await importCategories({
      category_ids: selectedCategories,
    });

    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occurred");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occurred");
    } else {
      toast.success(
        data?.message ||
          `Successfully imported ${selectedCategories.length} contribution category(ies)`
      );
      handleClose();
    }
  };

  const isAllSelected =
    filteredCategories.length > 0 &&
    selectedCategories.length === filteredCategories.length;

  return (
    <div className="py-3">
      <div className="mb-4">
        <h5 className="fw-bold mb-2 text-black1">Import Contribution Categories</h5>
        <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
          Select contribution categories from other branches in your company to import into
          your current branch
        </p>
      </div>

      {/* Search Input */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search contribution categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isError}
          style={{
            padding: "10px 15px",
            borderRadius: "5px",
            border: "1px solid #a1a0a0",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Categories List */}
      <CardComp
        cardClassName="border-0 rounded-10 px-0 mb-4 shadow3"
        cardBodyClassName="p-0"
      >
        {isLoading || isFetching ? (
          <div className="p-4">
            <TableSkeletonLoader rows={5} columns={1} />
          </div>
        ) : isError ? (
          <div className="p-4 text-center">
            <p className="text-danger mb-2 mb-md-3" style={{ fontSize: "14px" }}>
              {loadErrorMessage}
            </p>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {searchTerm
              ? "No contribution categories found matching your search"
              : "No contribution categories available from other branches"}
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {/* Select All Checkbox */}
            <div
              className="px-4 py-3 border-bottom"
              style={{
                backgroundColor: "#f7f7f7",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="selectAll"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  style={{ cursor: "pointer" }}
                />
                <label
                  className="form-check-label fw-bold text-black1"
                  htmlFor="selectAll"
                  style={{ cursor: "pointer", fontSize: "14px" }}
                >
                  Select All ({filteredCategories.length})
                </label>
              </div>
            </div>

            {/* Categories List */}
            {filteredCategories.map((category) => {
              const isSelected = selectedCategories.includes(category.id);
              const displayAmount =
                category.target_amount ?? category.amount;
              const branchLabel =
                category.branch_name ?? category.branch?.name;
              return (
                <div
                  key={category.id}
                  className={`px-4 py-3 border-bottom ${
                    isSelected ? "bg-light" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => handleToggleSelection(category.id)}
                >
                  <div className="d-flex align-items-center">
                    <div className="form-check me-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelection(category.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-black1 mb-0" style={{ fontSize: "14px" }}>
                          {category.name || "N/A"}
                        </h6>
                        <span
                          className="badge rounded-pill"
                          style={{
                            backgroundColor: category.is_withdrawable === 1 ? "#19bc31" : "#ff9500",
                            color: "white",
                            fontSize: "12px",
                            padding: "4px 10px",
                          }}
                        >
                          {category.is_withdrawable === 1 ? "Withdrawable" : "Non-Withdrawable"}
                        </span>
                      </div>
                      <div className="d-flex flex-wrap gap-3" style={{ fontSize: "12px" }}>
                        {displayAmount != null &&
                          displayAmount !== "" &&
                          !Number.isNaN(Number(displayAmount)) && (
                          <span className="text-muted">
                            Amount: {formatMoney(displayAmount)}
                          </span>
                        )}
                        {category.threshold && (
                          <span className="text-muted">
                            Threshold: {formatMoney(category.threshold)}
                          </span>
                        )}
                        {category.start_date && (
                          <span className="text-muted">
                            Start: {moment(category.start_date).format("DD-MMM-YYYY")}
                          </span>
                        )}
                        {category.end_date && (
                          <span className="text-muted">
                            End: {moment(category.end_date).format("DD-MMM-YYYY")}
                          </span>
                        )}
                        {branchLabel && (
                          <span className="text-muted">
                            Branch: {branchLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardComp>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-3">
        <ButtonComp
          btnText="Cancel"
          onClick={handleClose}
          btnClassName="bg-white py-2 px-4 rounded text-black1 border-2"
          style={{
            minWidth: "120px",
            borderColor: "var(--color-primary)",
            color: "var(--color-primary)",
          }}
        />
        <ButtonComp
          btnText={
            isImporting
              ? "Importing..."
              : `Import ${selectedCategories.length > 0 ? `(${selectedCategories.length})` : ""}`
          }
          onClick={handleImport}
          btnClassName="bg-primary1 py-2 px-4 rounded text-white border-0"
          style={{ minWidth: "150px" }}
          loading={isImporting}
          disabled={
            selectedCategories.length === 0 || isImporting || isError
          }
        />
      </div>
    </div>
  );
};

export default ImportContributionCategoriesModal;

